import Long from "long";
import { WalletErrorType } from "../../shared/error";
import { metaMaskWallet } from "../metaMask";
import { base64Image } from "./icon";
const walletInfo = {
    ...metaMaskWallet.info,
    name: "MetaMask",
    icon: base64Image,
    isChainSupported: (chainId) => {
        switch (chainId) {
            case "injective-1":
            case "injective-888":
                return false;
            default:
                return true;
        }
    },
    errors: {
        ...metaMaskWallet.info.errors,
        generic: [
            ...metaMaskWallet.info.errors.generic,
            {
                message: "User rejected the request.",
                type: WalletErrorType.rejected,
            },
        ],
    },
};
export const leapMetaMaskWallet = {
    provider: () => {
        const installed = typeof window.ethereum !== "undefined";
        if (!installed) {
            return { installed };
        }
        const metaMaskSnap = getMetaMaskSnap();
        return {
            installed,
            init: metaMaskSnap.init,
            enable: metaMaskSnap.enable,
            getKey: metaMaskSnap.getKey,
            getOfflineSigner: metaMaskSnap.getOfflineSigner,
            getOfflineSignerOnlyAmino: metaMaskSnap.getOfflineSignerOnlyAmino,
        };
    },
    info: walletInfo,
};
const getMetaMaskSnap = () => {
    const metaMaskSnap = {
        id: "npm:@leapwallet/metamask-cosmos-snap",
        params: undefined,
    };
    const ethereum = window.ethereum;
    const init = async () => {
        const clientVersion = await ethereum?.request({
            method: "web3_clientVersion",
        });
        const isMetamask = clientVersion.includes("MetaMask");
        if (!isMetamask) {
            throw new Error("Metamask is not installed");
        }
        if (typeof window.okxwallet !== "undefined") {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            if (window.okxwallet.isOkxWallet) {
                throw new Error("You have OKX Wallet installed. Please disable and reload the page to use Metamask Snap.");
            }
        }
        const version = clientVersion
            .split("MetaMask/v")[1]
            ?.split(".")[0];
        const isSupportMMSnap = Number(version) >= 11;
        if (!isSupportMMSnap) {
            throw new Error("Metamask Snap is not supported in this version");
        }
        await enable();
    };
    const enable = async () => {
        const installedSnap = await getSnap();
        if (!installedSnap) {
            await requestSnaps();
        }
    };
    const getSnaps = async () => {
        return (await ethereum?.request({
            method: "wallet_getSnaps",
        }));
    };
    const getSnap = async (version) => {
        try {
            const snaps = await getSnaps();
            return Object.values(snaps).find((snap) => {
                return (snap.id === metaMaskSnap.id && (!version || snap.version === version));
            });
        }
        catch {
            return undefined;
        }
    };
    const requestSnaps = async () => {
        await ethereum?.request({
            method: "wallet_requestSnaps",
            params: {
                [metaMaskSnap.id]: metaMaskSnap.params ?? {},
            },
        });
    };
    const metamaskSnapLeapKeysMap = {};
    const getKey = async (chainId) => {
        if (typeof metamaskSnapLeapKeysMap[chainId] !== "undefined") {
            return metamaskSnapLeapKeysMap[chainId];
        }
        const response = await ethereum?.request({
            method: "wallet_invokeSnap",
            params: {
                snapId: metaMaskSnap.id,
                request: {
                    method: "getKey",
                    params: {
                        chainId,
                    },
                },
            },
        });
        if (!response) {
            throw new Error("No response from MetaMask");
        }
        const key = response;
        // getKey from @leapwallet/cosmos-snap-provider return type is wrong
        if ("pubkey" in key && key.pubkey && typeof key.pubkey === "object") {
            const pubKey = Uint8Array.from(Object.values(key.pubkey));
            const { pubkey, ...values } = key;
            const fixedKey = {
                ...values,
                pubKey,
            };
            metamaskSnapLeapKeysMap[chainId] = fixedKey;
            return fixedKey;
        }
        throw new Error("Unhandled getKey case");
    };
    const getAccount = async (chainId) => {
        const key = await getKey(chainId);
        return {
            address: key.bech32Address,
            algo: key.algo,
            pubkey: key.pubKey,
        };
    };
    const requestSignAmino = async (chainId, signerAddress, signDoc) => {
        const signResponse = (await ethereum?.request({
            method: "wallet_invokeSnap",
            params: {
                snapId: metaMaskSnap.id,
                request: {
                    method: "signAmino",
                    params: {
                        chainId,
                        signerAddress,
                        signDoc,
                    },
                },
            },
        }));
        return signResponse;
    };
    const signAmino = async (...args) => {
        const [chainId, signer, signDoc, _signOptions] = args;
        const result = await requestSignAmino(chainId, signer, signDoc);
        return result;
    };
    const requestSignDirect = async (chainId, signerAddress, signDoc) => {
        const signature = (await ethereum?.request({
            method: "wallet_invokeSnap",
            params: {
                snapId: metaMaskSnap.id,
                request: {
                    method: "signDirect",
                    params: {
                        chainId,
                        signerAddress,
                        signDoc,
                    },
                },
            },
        }));
        const accountNumber = signDoc.accountNumber;
        if (!accountNumber) {
            throw new Error("Missing account number");
        }
        const modifiedAccountNumber = new Long(accountNumber.low, accountNumber.high, accountNumber.unsigned);
        return {
            signature: signature.signature,
            signed: {
                ...signature.signed,
                accountNumber: modifiedAccountNumber.toString(),
                authInfoBytes: new Uint8Array(Object.values(signature.signed.authInfoBytes)),
                bodyBytes: new Uint8Array(Object.values(signature.signed.bodyBytes)),
            },
        };
    };
    const signDirect = async (...args) => {
        const [chainId, signer, signDoc] = args;
        const res = await requestSignDirect(chainId, signer, signDoc);
        return res;
    };
    const getOfflineSignerOnlyAmino = (chainId) => {
        return {
            getAccounts: async () => [await getAccount(chainId)],
            signAmino: (signerAddress, signDoc) => signAmino(chainId, signerAddress, signDoc),
        };
    };
    const getOfflineSigner = (chainId) => {
        return {
            getAccounts: async () => [await getAccount(chainId)],
            signDirect: (signerAddress, signDoc) => signDirect(chainId, signerAddress, signDoc),
            signAmino: (signerAddress, signDoc) => signAmino(chainId, signerAddress, signDoc),
        };
    };
    return {
        init,
        enable,
        getKey,
        getOfflineSigner,
        getOfflineSignerOnlyAmino,
    };
};
//# sourceMappingURL=index.js.map